// src/pages/HomePage.jsx - Profound Philosophy
import React, { useState, lazy, Suspense } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';

// The Original Position's living city, for the featured section (lazy so
// none of the game's code rides in the main bundle).
const PromoVignette = lazy(() => import('../components/tools/originalPosition/PromoVignette'));
import {
  Brain,
  Dices,
  Sparkles,
  Target,
  Scale,
  Lightbulb,
  Heart,
  Eye,
  Users,
  Mountain,
  BookOpen,
  CheckCircle,
  ArrowRight,
  Clock,
  Award,
  TrendingUp,
  Zap,
  Shield,
  Coffee,
  AlertTriangle,
  Star,
  MessageSquare,
  Compass,
  Map,
  Globe
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  // Quick access for The Original Position's linked tables: a player handed
  // a room code can join straight from the front page. The living city is
  // lazy-loaded so no game code rides in the main bundle.
  const [gameCode, setGameCode] = useState('');
  const joinTable = () => {
    const code = gameCode.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 8);
    if (code.length >= 4) navigate(`/toolkit/original-position?join=${code}`);
  };
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 50]);
  const y2 = useTransform(scrollY, [0, 300], [0, -50]);

  const philosophicalProfiles = [
    {
      icon: Brain,
      title: "The Questioner",
      description: "You dig beneath surface claims and challenge assumptions like Socrates",
      color: "blue"
    },
    {
      icon: Target,
      title: "The Logic Master",
      description: "You spot flaws in reasoning and build bulletproof arguments",
      color: "green"
    },
    {
      icon: Eye,
      title: "The Truth Seeker",
      description: "You evaluate evidence and distinguish knowledge from opinion",
      color: "purple"
    },
    {
      icon: Heart,
      title: "The Moral Compass",
      description: "You navigate ethical complexity with systematic frameworks",
      color: "red"
    },
    {
      icon: Users,
      title: "The Identity Explorer",
      description: "You grapple with personal identity and questions of free will",
      color: "cyan"
    },
    {
      icon: Lightbulb,
      title: "The Consciousness Investigator",
      description: "You're fascinated by the mystery of subjective experience",
      color: "yellow"
    },
    {
      icon: Scale,
      title: "The Justice Advocate",
      description: "You're drawn to questions of fairness and political philosophy",
      color: "indigo"
    },
    {
      icon: Mountain,
      title: "The Meaning Maker",
      description: "You create purpose in an absurd universe like Camus",
      color: "orange"
    }
  ];

  const transformations = [
    {
      from: "Feeling lost in philosophical jargon",
      to: "Speaking the language of history's greatest thinkers",
      icon: BookOpen
    },
    {
      from: "Uncertain in ethical dilemmas",
      to: "Navigating moral complexity with confidence",
      icon: Heart
    },
    {
      from: "Accepting what you're told",
      to: "Questioning assumptions like Socrates",
      icon: MessageSquare
    },
    {
      from: "Living on autopilot",
      to: "Creating meaning through conscious choice",
      icon: Mountain
    }
  ];

  // Keep in step with src/data/modules.js — these are the real module titles.
  const modules = [
    {
      number: "01",
      title: "What Is Philosophy?",
      description: "The art of asking better questions, and spotting philosophy hiding in daily life",
      tools: "Three-Question Filter, Socratic Dialogue Journal"
    },
    {
      number: "02",
      title: "Logic & Clear Thinking",
      description: "Build valid arguments and spot fallacies instantly",
      tools: "Fallacy Detective, Argument Builder"
    },
    {
      number: "03",
      title: "Epistemology: What Can You Really Know?",
      description: "What can you really know? Evaluate evidence and calibrate confidence",
      tools: "CREDIBLE Evaluator, Confidence Calibrator"
    },
    {
      number: "04",
      title: "Ethics & Moral Philosophy",
      description: "Reason through real dilemmas with the classic moral frameworks",
      tools: "Framework Analyser, Personal Ethics Auditor"
    },
    {
      number: "05",
      title: "Metaphysics & Reality",
      description: "Free will, personal identity, time, and what's actually real",
      tools: "Identity Explorer, Free Will Tracker"
    },
    {
      number: "06",
      title: "Philosophy of Mind & Consciousness",
      description: "Consciousness, the hard problem, and whether AI could ever understand",
      tools: "Qualia Journal, Chinese Room Interactive"
    },
    {
      number: "07",
      title: "Social & Political Philosophy",
      description: "Justice, rights, liberty, and how we manage to live together",
      tools: "Veil of Ignorance Analyser, Philosophy Mapper"
    },
    {
      number: "08",
      title: "Existentialism & Meaning",
      description: "Create meaning through authentic choice",
      tools: "Existential Choice Analyser, Meaning Workshop"
    }
  ];

  const profoundApps = [
    {
      title: "Profound Thinking",
      description: "Critical thinking for real decisions, from bias-spotting to thinking in systems, with a calibration engine that scores your confidence against reality.",
      icon: Brain,
      color: "blue",
      features: ["17 modules incl. Thinking with AI", "Calibration engine", "20 practice tools", "Daily drills & challenges"],
      cta: "Visit Profound Thinking",
      href: "https://profoundthinking.divedeepdialogues.com",
      // Literal classes (not `bg-${color}-…`) so Tailwind's scanner sees them
      ctaClass: "bg-blue-700 hover:bg-blue-800"
    },
    {
      title: "Profound Living",
      description: "The examined life, lived like it ends: meaning, mortality, legacy and grief, taught with unusual honesty and warmth.",
      icon: Heart,
      color: "pink",
      features: ["8 deep modules", "27-question Living profile", "7-tool practice Toolkit", "Willow, an AI companion"],
      cta: "Visit Profound Living",
      href: "https://profoundliving.divedeepdialogues.com",
      ctaClass: "bg-pink-700 hover:bg-pink-800"
    },
    {
      title: "Profound Questions",
      description: "The art of asking better questions: Socratic method, coaching inquiry, and the questions that open people up instead of closing them down.",
      icon: MessageSquare,
      color: "cyan",
      features: ["8 question-craft modules", "Socratic & coaching methods", "Question practice tools", "Quinn, an AI companion"],
      cta: "Visit Profound Questions",
      href: "https://profoundquestions.divedeepdialogues.com",
      ctaClass: "bg-cyan-700 hover:bg-cyan-800"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white pt-24">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32">
        {/* The thinking head: cloud-00, the sunlit tower, feathered into the canvas */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img
            src="/images/cloud-00.webp"
            alt=""
            className="w-full h-full object-cover object-[70%_30%] opacity-70"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/55 via-slate-900/40 to-slate-900" />
        </div>
        {/* Animated Background */}
        <motion.div
          style={{ y: y1 }}
          className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"
        />
        <motion.div
          style={{ y: y2 }}
          className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500/10 border border-purple-500/20 rounded-full mb-8"
            >
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-purple-300 font-medium">
                Discover your unique philosophical profile in 5 minutes
              </span>
            </motion.div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Think Like{' '}
              <span className="bg-gradient-to-r from-purple-300 via-pink-300 to-indigo-300 bg-clip-text text-transparent">
                Socrates, Camus & Rawls
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-purple-200 mb-8 max-w-3xl mx-auto">
              Philosophy isn't just for academics in dusty libraries. It's the art of asking better questions,
              navigating ethical complexity, and creating meaning in your life.
              <strong className="text-white"> Join the ranks of history's deepest thinkers.</strong>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/assessment">
                <motion.span
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-4 rounded-xl text-lg font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Sparkles className="w-6 h-6" />
                  Discover Your Philosophical Profile
                  <span className="text-sm bg-black/30 px-2 py-1 rounded-full">5 min</span>
                </motion.span>
              </Link>
              <Link to="/modules">
                <motion.span
                  className="bg-slate-700 hover:bg-slate-600 text-white px-8 py-4 rounded-xl text-lg font-semibold flex items-center gap-2 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <BookOpen className="w-6 h-6" />
                  Explore the Modules
                </motion.span>
              </Link>
            </div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap justify-center gap-6 text-sm text-purple-300"
            >
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                No signup required
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                18 interactive tools
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Sources checked (but never boring)
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured: The Original Position, the justice game */}
      <section className="relative py-14">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto rounded-3xl border-2 border-bright-gold/50 bg-gradient-to-r from-sky-900/40 via-slate-900/70 to-violet-900/40 p-6 sm:p-10"
          >
            {/* grid-cols-1 at base: without it the implicit auto track sizes to
                the vignette's min-content and pushes the page past 320px. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-bright-gold mb-3">
                  Featured · The justice game
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">The Original Position</h2>
                <p className="text-lg text-indigo-200 font-medium mb-4">
                  Build the world. Then be born into it.
                </p>
                <p className="text-sm text-slate-300 mb-5">
                  Design a society with fourteen high-stakes levers, armed with exactly one true
                  hint about who you’ll be, then get born into your own rules as a random citizen,
                  take fate’s swing, and find out which philosopher you played. Play solo, pass one
                  phone around the table, or host a linked table where everyone joins by QR.
                </p>
                <div className="flex flex-wrap gap-2 mb-6 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-slate-900/60 border border-slate-600 text-slate-300">Solo · 10–15 mins</span>
                  <span className="px-2.5 py-1 rounded-full bg-slate-900/60 border border-slate-600 text-slate-300">Parlour · one phone</span>
                  <span className="px-2.5 py-1 rounded-full bg-slate-900/60 border border-slate-600 text-slate-300">Linked table · QR + phones</span>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <Link
                    to="/toolkit/original-position"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-bright-gold text-charcoal font-bold hover:bg-white transition-colors"
                  >
                    <Dices className="w-5 h-5" />
                    Play the game
                  </Link>
                  <Link
                    to="/toolkit/original-position"
                    className="text-sm text-bright-gold font-semibold hover:underline"
                  >
                    Host a table
                    <ArrowRight className="w-4 h-4 inline ml-1" />
                  </Link>
                </div>
              </div>
              <div className="space-y-4">
                <Suspense fallback={<div className="rounded-2xl border border-slate-700 bg-gunmetal min-h-[14rem]" />}>
                  <PromoVignette />
                </Suspense>
                <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-700">
                  <p className="text-xs text-slate-400 mb-2">
                    Handed a room code? Your table is waiting:
                  </p>
                  <div className="flex gap-2">
                    <input
                      value={gameCode}
                      onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === 'Enter') joinTable(); }}
                      placeholder="CODE"
                      maxLength={8}
                      aria-label="Room code"
                      className="flex-1 min-w-0 px-3 py-2 bg-slate-950/60 border border-slate-600 text-white placeholder-slate-500 rounded-lg text-center tracking-[0.25em] font-mono focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    />
                    <button
                      onClick={joinTable}
                      className="px-4 py-2 rounded-lg bg-bright-gold text-charcoal font-semibold text-sm hover:bg-white transition-colors"
                    >
                      Join
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Type of Thinker Are You? */}
      <section className="relative overflow-hidden py-20 bg-slate-900/50 backdrop-blur">
        {/* Pre-dawn sky: you are about to find something out. Decorative only, feathered into the canvas, PL's technique. */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('/images/cloud-assessment.webp')",
              backgroundSize: 'cover',
              backgroundPosition: 'center 40%',
              opacity: 0.30,
              maskImage: 'radial-gradient(ellipse 85% 80% at 50% 45%, black 32%, transparent 78%)',
              WebkitMaskImage: 'radial-gradient(ellipse 85% 80% at 50% 45%, black 32%, transparent 78%)'
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              What Type of{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Philosophical Thinker
              </span>{' '}
              Are You?
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              Take our 5-minute assessment to discover which philosophical profile matches your natural thinking style.
              Are you a Questioner like Socrates? A Meaning Maker like Camus? Find out now.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {philosophicalProfiles.map((profile, idx) => {
              const Icon = profile.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 hover:border-purple-500/50 transition-all group"
                >
                  <div className={`w-12 h-12 bg-${profile.color}-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-6 h-6 text-${profile.color}-400`} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{profile.title}</h3>
                  <p className="text-sm text-purple-200">{profile.description}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/assessment" className="inline-block">
              <span className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-4 rounded-xl text-lg font-semibold inline-flex items-center gap-2 shadow-lg">
                Take the Assessment
                <ArrowRight className="w-5 h-5" />
              </span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Transformation Section */}
      <section className="relative overflow-hidden py-20 bg-gradient-to-br from-purple-900/20 to-indigo-900/20">
        {/* Mountains breaking through cloud: the change the modules make. Decorative only. */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('/images/cloud-03.webp')",
              backgroundSize: 'cover',
              backgroundPosition: 'center 55%',
              opacity: 0.26,
              maskImage: 'radial-gradient(ellipse 90% 85% at 30% 50%, black 28%, transparent 80%)',
              WebkitMaskImage: 'radial-gradient(ellipse 90% 85% at 30% 50%, black 28%, transparent 80%)'
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            {/* break-words: under forced letter-spacing (WCAG 1.4.12) the single
                word no longer fits 320px and must be able to wrap, not clip. */}
            <h2 className="text-4xl md:text-5xl font-bold mb-4 break-words">
              Your Philosophical{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Transformation
              </span>
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              Philosophy isn't about memorising names and dates. It's about transformation.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {transformations.map((transform, idx) => {
              const Icon = transform.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6"
                >
                  <div className="flex items-start gap-4">
                    <Icon className="w-8 h-8 text-purple-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-sm text-red-300 mb-1">❌ From:</div>
                      <p className="text-slate-300 mb-3">{transform.from}</p>
                      <div className="text-sm text-green-400 mb-1">✅ To:</div>
                      <p className="text-white font-semibold">{transform.to}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Your Philosophy, On the Record */}
      <section className="relative overflow-hidden py-20 bg-slate-900/50 backdrop-blur">
        {/* Stars above the cloud deck: the voices you take a side among. Decorative only. */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('/images/cloud-05.webp')",
              backgroundSize: 'cover',
              backgroundPosition: 'center 25%',
              opacity: 0.30,
              maskImage: 'radial-gradient(ellipse 90% 85% at 72% 50%, black 28%, transparent 80%)',
              WebkitMaskImage: 'radial-gradient(ellipse 90% 85% at 72% 50%, black 28%, transparent 80%)'
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Reading Becomes{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Taking a Side
              </span>
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              You don't just learn what philosophers said: the modules stop at twelve classic
              forks and ask what <em>you</em> say. Your answers assemble into a philosophy of
              your own, tensions and all.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 hover:border-violet-500/50 transition-all"
            >
              <Compass className="w-8 h-8 text-violet-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Your Philosophy, So Far</h3>
              <p className="text-sm text-purple-200 mb-4">
                Twelve forks: the trolley problem, the dream test, free will and more. Take a
                position at each, then watch for the tensions between your own answers.
              </p>
              <Link to="/positions" className="text-sm text-violet-300 hover:text-white inline-flex items-center gap-1 transition-colors">
                See your positions <ArrowRight className="w-3 h-3" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 hover:border-sky-500/50 transition-all"
            >
              <Map className="w-8 h-8 text-sky-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">The Lineage</h3>
              <p className="text-sm text-purple-200 mb-4">
                More than fifty voices across twenty-six centuries, answering each other, and lighting up
                as you properly meet them, module by module.
              </p>
              <Link to="/lineage" className="text-sm text-sky-300 hover:text-white inline-flex items-center gap-1 transition-colors">
                Walk the lineage <ArrowRight className="w-3 h-3" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all"
            >
              <Globe className="w-8 h-8 text-amber-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Voices From Everywhere</h3>
              <p className="text-sm text-purple-200 mb-4">
                Confucius beside Kant, the Buddha beside Descartes, Rumi, Gandhi and Ubuntu
                justice. Philosophy has many births, and the modules honour them.
              </p>
              <Link to="/modules" className="text-sm text-amber-300 hover:text-white inline-flex items-center gap-1 transition-colors">
                Meet them in the modules <ArrowRight className="w-3 h-3" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 8 Modules Overview */}
      <section className="relative overflow-hidden py-20 bg-slate-900/50">
        {/* The clearing after rain: practice done, clarity earned. Decorative only. */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('/images/cloud-toolkit.webp')",
              backgroundSize: 'cover',
              backgroundPosition: 'center 45%',
              opacity: 0.26,
              maskImage: 'radial-gradient(ellipse 85% 80% at 50% 45%, black 32%, transparent 78%)',
              WebkitMaskImage: 'radial-gradient(ellipse 85% 80% at 50% 45%, black 32%, transparent 78%)'
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              8 Modules,{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                18 Interactive Tools
              </span>
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              From Socratic questioning to existential meaning-making, master the philosophical skills
              that matter. Each module includes practical tools to embed your learning.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {modules.map((module, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 hover:border-purple-500/50 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="text-3xl font-bold text-purple-300/80">{module.number}</div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{module.title}</h3>
                    <p className="text-purple-200 mb-3">{module.description}</p>
                    <div className="text-sm text-purple-300 bg-purple-900/20 rounded-lg px-3 py-2">
                      🛠️ Tools: {module.tools}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/modules" className="inline-block">
              <span className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-4 rounded-xl text-lg font-semibold inline-flex items-center gap-2">
                Explore All Modules
                <ArrowRight className="w-5 h-5" />
              </span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Meet Sofia, the sparring partner. The modules above establish the
          content; this establishes the person you argue it with. The mock
          exchange does the selling: one look and you know what sparring means. */}
      <section className="relative overflow-hidden py-20 bg-gradient-to-br from-indigo-900/30 to-slate-900/60">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-bright-gold text-sm font-bold uppercase tracking-widest mb-3">Meet Sofia</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5">
                A sparring partner who has read widely and never gets tired
              </h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                Thinking sharpens fastest against resistance, and thoughtful resistance is
                hard to find; most dinner tables change the subject. Sofia takes the other
                side. On your invitation, and only then: put a view to her and she will argue
                against it, remember what you believe, and pick the thread up next time.
              </p>
              <p className="text-slate-300 leading-relaxed mb-6">
                At the end of every module section there is a quiet handoff: finish the
                sentence, press send, and defend yourself.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/companion"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-conversation-teal to-bright-gold text-gunmetal rounded-lg font-bold shadow-lg hover:brightness-110 hover:shadow-xl transition-all"
                >
                  Spar with Sofia
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <span className="text-slate-400 text-sm">A whetstone, not an oracle.</span>
              </div>
              <p className="text-slate-400 text-xs mt-3">
                A few rounds free every day. You'll just need to sign in, so she can
                remember what you believe and how the argument went last time.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="flex justify-end">
                <div className="max-w-[85%] bg-conversation-teal/20 border border-conversation-teal/40 rounded-2xl rounded-br-sm px-4 py-3">
                  <p className="text-slate-100 text-sm leading-relaxed">
                    Free will survives determinism. My choices are still mine.
                  </p>
                </div>
              </div>
              <div className="flex items-end gap-3">
                <img
                  src="/images/characters/sofia.webp"
                  alt="Sofia, the app's philosophy companion"
                  className="w-10 h-10 rounded-full border-2 border-bright-gold/60 object-cover flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="max-w-[85%] bg-slate-800/80 border border-bright-gold/30 rounded-2xl rounded-bl-sm px-4 py-3">
                  <p className="text-slate-200 text-sm leading-relaxed">
                    Then let's test "mine". You didn't choose your genes, your upbringing,
                    or the mood you woke up in this morning, so where, exactly, does the
                    ownership enter?
                  </p>
                </div>
              </div>
              <p className="text-slate-400 text-xs text-right italic">Your move.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Profound Apps */}
      <section className="py-20 bg-gradient-to-br from-blue-900/20 to-purple-900/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-blue-300 text-sm font-medium">Part of the Profound Series</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Complete Your{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Profound Journey
              </span>
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              Profound Philosophy is part of a series of apps designed to help you think more clearly,
              live more intentionally, and create a life aligned with your deepest values.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {profoundApps.map((app, idx) => {
              const Icon = app.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.15 }}
                  className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-8 hover:border-blue-500/50 transition-all"
                >
                  <div className={`w-16 h-16 bg-${app.color}-900/30 rounded-2xl flex items-center justify-center mb-4`}>
                    <Icon className={`w-8 h-8 text-${app.color}-400`} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{app.title}</h3>
                  <p className="text-purple-200 mb-4">{app.description}</p>
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {app.features.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-sm text-purple-300">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  <a
                    href={app.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full ${app.ctaClass} text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2`}
                  >
                    {app.cta}
                    <ArrowRight className="w-5 h-5" />
                  </a>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden py-20 bg-slate-900/70">
        {/* Dusk with one ember low: the bookend to the sunlit tower up top. Decorative only. */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('/images/cloud-journal.webp')",
              backgroundSize: 'cover',
              backgroundPosition: 'center bottom',
              opacity: 0.32,
              maskImage: 'radial-gradient(ellipse 90% 85% at 30% 50%, black 28%, transparent 80%)',
              WebkitMaskImage: 'radial-gradient(ellipse 90% 85% at 30% 50%, black 28%, transparent 80%)'
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Think{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Like a Philosopher?
              </span>
            </h2>
            <p className="text-xl text-purple-200 mb-8">
              Be among the first to map your philosophical profile and start mastering the art of deep thinking.
              It takes about 5 minutes.
            </p>

            <Link to="/assessment" className="inline-block">
              <span className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-6 sm:px-12 py-5 rounded-xl text-xl font-semibold inline-flex flex-wrap justify-center items-center gap-3 max-w-full shadow-2xl">
                <Sparkles className="w-7 h-7" />
                Discover Your Philosophical Profile
                <ArrowRight className="w-7 h-7" />
              </span>
            </Link>

            <p className="text-sm text-purple-300 mt-6">
              No credit card • No signup • Results saved locally • 5 minutes
            </p>
          </motion.div>
        </div>
      </section>

      {/* The warning is real: deep reading measurably borrows attention
          from the mundane, and readers do come back to kettles feeling odd;
          it just happens to be funnier stated as a caution. */}
      <section className="py-12 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto rounded-xl border border-amber-400/40 bg-amber-500/10 p-6"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <h3 className="text-amber-300 font-bold uppercase tracking-widest text-sm mb-2">A small warning</h3>
                <p className="text-slate-300 leading-relaxed">
                  Sustained philosophy is known to produce temporary side effects. The world may
                  briefly feel less solid than advertised. Ordinary objects may misplace their
                  names. You may pour the coffee while privately doubting the cup. All of this
                  is normal, passes within minutes, and is widely regarded as a sign the
                  treatment is working.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Honest Note */}
      <section className="py-12 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h3 className="text-xl font-semibold text-white mb-4">A Note on Authenticity</h3>
            <p className="text-purple-200 leading-relaxed">
              This app is brand new, so we don't have thousands of testimonials yet.
              What we do have is content checked against its sources in an October 2026 audit,
              18 interactive tools built with genuine care, and a mission to make philosophy accessible
              and practical. Give it a try; your feedback helps us make it better.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
